// frontend/src/components/TaxiMeter.js
/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Card, Alert, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';

// Haversine formula to calculate distance between two coordinates in miles
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Calculate fare based on distance and tariff
const calculateFare = (distance, tariff) => {
  if (!tariff) return 0;
  
  const firstMile = tariff.firstMile || 0;
  const subsequentMile = tariff.subsequentMile || 0;
  
  // Taxi meter starts with first mile rate (even at distance 0)
  if (distance <= 0) {
    return firstMile;
  }
  
  // If distance is within first mile, return first mile rate
  if (distance <= 1) {
    return firstMile;
  }
  
  // Calculate fare: first mile + (additional miles * subsequent mile rate)
  const extraMiles = distance - 1;
  return firstMile + (extraMiles * subsequentMile);
};

export default function TaxiMeter() {
  const token = localStorage.getItem('token');
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [driverLicensingAuthority, setDriverLicensingAuthority] = useState(null);
  const [licensingBody, setLicensingBody] = useState(null);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [distance, setDistance] = useState(0); // in miles
  const [fare, setFare] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0); // in seconds
  
  // Refs for tracking
  const watchIdRef = useRef(null);
  const previousLocationRef = useRef(null);
  const startTimeRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const totalDistanceRef = useRef(0);
  const selectedTariffRef = useRef(null);

  // Fetch driver profile and licensing body
  useEffect(() => {
    const fetchDriverData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch driver profile to get licensing authority
        const profileResponse = await axios.get('/user/profile');
        
        console.log('Profile response:', profileResponse.data);
        let licensingAuthority = profileResponse.data?.licensingAuthority;
        
        console.log('Raw licensingAuthority:', licensingAuthority);
        
        // Handle different possible values
        if (!licensingAuthority || licensingAuthority.trim() === '') {
          console.error('No licensing authority found in profile');
          setError('Please set your licensing authority in your profile settings (Admin > Profile).');
          setLoading(false);
          return;
        }
        
        // Convert to uppercase for code lookup (should already be a code from dropdown)
        licensingAuthority = licensingAuthority.trim().toUpperCase();
        console.log('Processed licensingAuthority:', licensingAuthority);
        setDriverLicensingAuthority(licensingAuthority);

        // Fetch licensing body by code
        let licensingBodyResponse;
        try {
          licensingBodyResponse = await axios.get(`/licensing-bodies/code/${licensingAuthority}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (err) {
          console.error('Error fetching licensing body:', err);
          if (err.response && err.response.status === 404) {
            setError(`Licensing authority "${licensingAuthority}" not found in System Administration > Fare Tariffs. Please update your profile or contact an administrator.`);
          } else {
            setError('Failed to load licensing authority information. Please try again.');
          }
          setLoading(false);
          return;
        }
        
        setLicensingBody(licensingBodyResponse.data);
        
        // Set default tariff to "Tariff 1", or first tariff if "Tariff 1" doesn't exist
        const tariffs = licensingBodyResponse.data.tariffs || [];
        const tariff1 = tariffs.find(t => t.name === 'Tariff 1' || t.name === 'Tariff 1:- Weekday Daytime Rate');
        const defaultTariff = tariff1 || tariffs.find(t => t.active !== false) || tariffs[0];
        if (defaultTariff) {
          setSelectedTariff(defaultTariff);
        } else {
          setError('No tariffs available for your licensing authority');
        }
      } catch (err) {
        console.error('Error fetching driver data:', err);
        setError('Failed to load licensing authority or tariff information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDriverData();
  }, [token]);

  // Start the meter
  const handleStart = () => {
    if (!selectedTariff) {
      alert('No tariff selected. Please contact support.');
      return;
    }

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }

    // Reset values
    setDistance(0);
    setFare(0);
    setElapsedTime(0);
    totalDistanceRef.current = 0;
    previousLocationRef.current = null;
    startTimeRef.current = Date.now();

    // Start timer (continues even when tab is not visible)
    timerIntervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);

    // Start watching position (continues even when tab is not visible)
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        if (previousLocationRef.current) {
          // Calculate distance from previous position
          const dist = calculateDistance(
            previousLocationRef.current.latitude,
            previousLocationRef.current.longitude,
            latitude,
            longitude
          );
          
          // Only add distance if it's significant (filter out GPS noise)
          if (dist > 0.001) { // 0.001 miles ≈ 5.3 feet
            totalDistanceRef.current += dist;
            setDistance(totalDistanceRef.current);
            
            // Calculate and update fare using current tariff from ref
            const currentTariff = selectedTariffRef.current;
            if (currentTariff) {
              const newFare = calculateFare(totalDistanceRef.current, currentTariff);
              setFare(newFare);
            }
          }
        }
        
        // Update previous location
        previousLocationRef.current = { latitude, longitude };
      },
      (error) => {
        console.error('Geolocation error:', error);
        if (error.code === error.PERMISSION_DENIED) {
          alert('Location permission denied. Please enable location services and try again.');
          handleStop();
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          alert('Location unavailable. Please check your GPS signal.');
        } else if (error.code === error.TIMEOUT) {
          console.warn('Location request timeout');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000, // Increased timeout for background operation
        maximumAge: 2000 // Allow slightly older positions for background operation
      }
    );

    setIsRunning(true);
  };

  // Stop the meter
  const handleStop = () => {
    // Stop watching position
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    // Stop timer
    if (timerIntervalRef.current !== null) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    setIsRunning(false);
  };

  // Reset the meter
  const handleReset = () => {
    handleStop();
    setDistance(0);
    setElapsedTime(0);
    totalDistanceRef.current = 0;
    previousLocationRef.current = null;
    startTimeRef.current = null;
    // Reset fare to first mile rate (meter starts with first mile charge)
    if (selectedTariff) {
      const initialFare = calculateFare(0, selectedTariff);
      setFare(initialFare);
    } else {
      setFare(0);
    }
  };

  // Update ref when tariff changes
  useEffect(() => {
    selectedTariffRef.current = selectedTariff;
    // Also recalculate fare if meter is running
    if (selectedTariff && distance > 0) {
      const newFare = calculateFare(distance, selectedTariff);
      setFare(newFare);
    }
  }, [selectedTariff, distance]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (timerIntervalRef.current !== null) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Handle tariff toggle
  const handleTariffToggle = () => {
    if (!licensingBody || !licensingBody.tariffs || licensingBody.tariffs.length === 0) return;
    
    const tariffs = licensingBody.tariffs.filter(t => t.active !== false);
    if (tariffs.length <= 1) return;
    
    // Find current tariff index
    const currentIndex = tariffs.findIndex(t => t._id === selectedTariff?._id || t.name === selectedTariff?.name);
    
    // Get next tariff (wrap around)
    const nextIndex = (currentIndex + 1) % tariffs.length;
    setSelectedTariff(tariffs[nextIndex]);
  };

  // Get tariff display name (e.g., "Tariff 1", "Tariff 2")
  const getTariffDisplayName = (tariff) => {
    if (!tariff) return '';
    // Extract tariff number from name (e.g., "Tariff 1" or "Tariff 1:- Weekday Daytime Rate")
    const match = tariff.name.match(/tariff\s*(\d+)/i);
    if (match) {
      return `Tariff ${match[1]}`;
    }
    return tariff.name;
  };

  // Get current tariff display name for button
  const getCurrentTariffDisplayName = () => {
    if (!selectedTariff) return '';
    return getTariffDisplayName(selectedTariff);
  };

  // Format time display
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading taxi meter...</p>
      </Container>
    );
  }

  // Function to clean tariff description (remove "TARIFF n:-" prefix)
  const cleanTariffDescription = (description) => {
    if (!description) return '';
    // Remove pattern like "TARIFF 1:- " or "TARIFF 1:" or "TARIFF 1 -" at the start
    return description.replace(/^TARIFF\s+\d+\s*[:-]\s*/i, '').trim();
  };

  return (
    <Container>
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {licensingBody && (
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-center mb-3">
              {licensingBody.tariffs && licensingBody.tariffs.filter(t => t.active !== false).length > 1 ? (
                <Button
                  variant="outline-primary"
                  onClick={handleTariffToggle}
                  disabled={isRunning}
                  size="lg"
                >
                  {getCurrentTariffDisplayName()}
                </Button>
              ) : (
                <div className="text-center">
                  <span className="badge bg-secondary fs-6 px-3 py-2">
                    {getCurrentTariffDisplayName()}
                  </span>
                </div>
              )}
            </div>
            {selectedTariff && (
              <div className="text-center">
                <p className="mb-1"><strong>Authority:</strong> {licensingBody.name} ({licensingBody.code})</p>
                {selectedTariff.description && (
                  <p className="text-muted small mb-0">{cleanTariffDescription(selectedTariff.description)}</p>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      <Card className="mb-4" style={{ backgroundColor: '#f8f9fa' }}>
        <Card.Body>
          <div className="row text-center">
            <div className="col-md-6 mb-3 mb-md-0">
              <h3 className="mb-2" style={{ fontSize: '3rem', fontWeight: 'bold', color: '#28a745' }}>
                £{fare.toFixed(2)}
              </h3>
              <p className="text-muted mb-3">Current Fare</p>
              <div className="d-flex gap-2 justify-content-center flex-wrap">
                {!isRunning ? (
                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleStart}
                    disabled={!selectedTariff}
                  >
                    Start Meter
                  </Button>
                ) : (
                  <Button
                    variant="danger"
                    size="lg"
                    onClick={handleStop}
                  >
                    Stop Meter
                  </Button>
                )}
                <Button
                  variant="outline-secondary"
                  size="lg"
                  onClick={handleReset}
                  disabled={isRunning}
                >
                  Reset
                </Button>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-2">
                <h6 className="text-muted mb-1">Distance & Time</h6>
                <h4 className="mb-2">{distance.toFixed(2)} miles | {formatTime(elapsedTime)}</h4>
              </div>
              <div>
                <h6 className="text-muted mb-1">Status</h6>
                <h4 className="mb-0">
                  <span className={`badge ${isRunning ? 'bg-success' : 'bg-secondary'}`}>
                    {isRunning ? 'Running' : 'Stopped'}
                  </span>
                </h4>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>Disclaimer:</strong> This is for emergency use only, and is not calibrated. Error ± £2.
      </Alert>
    </Container>
  );
}

